import { Delete } from '@mui/icons-material';
import { Box, Stack } from '@mui/material';
import {
  Button,
  FileInput,
  FileLink,
  SortableList,
} from 'melp-design/components';
import { SystemColors } from 'melp-design/style';
import { FC } from 'react';
import { Control, Controller, useFieldArray } from 'react-hook-form-latest';
import { useTranslation } from 'react-i18next';
import { useUploadFile } from 'store/files';
import { Values } from '../../survey-content.types';

export const AttachmentsList: FC<{
  control: Control<Values>;
  translationIndex: number;
  disabled?: boolean;
}> = ({ control, translationIndex, disabled }) => {
  const { t } = useTranslation();

  const {
    fields: attachmentFields,
    append: appendAttachment,
    move: moveAttachments,
    remove: removeAttachment,
  } = useFieldArray({
    control,
    name: `translations.${translationIndex}.attachments`,
  });

  const { mutate: uploadFile, isLoading: isUploading } = useUploadFile();

  return (
    <Controller
      name={`translations.${translationIndex}.attachments`}
      control={control}
      rules={{
        required: {
          value: true,
          message: t('form.required'),
        },
      }}
      render={({ field: { name, value, onChange, ref }, fieldState }) => (
        <Stack gap={1}>
          <FileInput
            name={name}
            label={t('form.attachments')}
            onChange={([file]) => {
              if (file) {
                uploadFile(
                  { file },
                  {
                    onSuccess: ({ data }) => {
                      appendAttachment({ fileId: data.id, name: file.name });
                    },
                  },
                );
              } else {
                onChange(value);
              }
            }}
            disabled={isUploading || disabled}
            maxFiles={1}
            previewSelectedFiles={false}
            accept={[
              'jpg',
              'jpeg',
              'png',
              'gif',
              'webp',
              'txt',
              'pdf',
              'doc',
              'docx',
              'xls',
              'xlsx',
              'ppt',
              'pptx',
            ]}
            error={fieldState.error?.root?.message || fieldState.error?.message}
            ref={ref}
          />

          <SortableList
            key={JSON.stringify(attachmentFields.map((field) => field.id))}
            onPositionChange={moveAttachments}
            disabled={disabled}
            items={attachmentFields.map((field, i) => ({
              id: field.id,
              content: (
                <Stack
                  direction="row"
                  alignItems="center"
                  py={0.5}
                  px={2}
                  spacing={2}
                  sx={{ backgroundColor: SystemColors.white }}
                  borderRadius={1}
                  boxShadow={`0 0 0 1px ${SystemColors.grey[90]}`}
                  key={field.id}
                >
                  <SortableList.DragIndicator />

                  <Box flex={1}>
                    <FileLink id={field.fileId} />
                  </Box>

                  <Box flexShrink={0}>
                    <Button
                      variant="neutral-outline"
                      size="md"
                      icon={Delete}
                      onClick={() => removeAttachment(i)}
                      title={t('common.remove')}
                      disabled={disabled}
                    />
                  </Box>
                </Stack>
              ),
            }))}
          />
        </Stack>
      )}
    />
  );
};
